import { useIntl } from 'react-intl';
import { useContext, useEffect, useRef, useState } from 'react';
import { Progress } from 'antd';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';

import {
  QUICK_FILTERS_VALUES,
  ROUTES_ANALYTICS_CARDS,
  ROUTES_TABLE_COLUMNS,
  ROUTES_TABLE_QUICK_FILTERS,
  ROUTE_EXPORT_TYPE,
  ROUTE_STATES_TAG_COLOR
} from 'constants/route';
import { STAR_AVAILABILITY_STATE } from 'constants/stars';
import { getRoutes } from 'services/hubs';
import { exportRoutes, getRoutesAnalytics } from 'services/routes-form';
import { fetchStars } from 'services/live-ops-priorities';
import { formatOptions } from 'utils';
import { isUserAuthorized } from 'utils/helpers';
import aclMatrix from 'common/aclMatrix';
import { ACL_MATRIX } from 'common/permissions';
import { getCurrentUserHubInfo } from 'utils/hubs';
import { getAllAvailableStars } from 'actions/StarAction';

import RoutesTableActions from './components/RoutesTableActions/RoutesTableActions';
import BRTable from 'components/BRTable/BRTable';
import BRHeader from 'components/BRHeader/BRHeader';
import BRTag from 'components/BRTag/BRTag';
import Container from 'components/Container/index';
import RoutesSearch from './components/RoutesSearch/RoutesSearch';
import { notify } from 'components/Notify/Notify';
import SingleInfoCard from 'components/OpsControl/SingleCardInfo/SingleCardInfo';

import './RoutesListing.less';
import { HubsContext } from 'contexts/hub.context';

const RoutesListing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [routesCount, setRoutesCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [onlineStars, setOnlineStars] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [analyltics, setAnalytics] = useState(null);
  const [searchCleared, setSearchCleared] = useState(false);
  const [currentQuickFilter, setCurrentQuickFilter] = useState();
  const refreshTable = useRef();
  const userHubInfo = getCurrentUserHubInfo();
  const userWareHouse = userHubInfo?._id;
  const firstRender = useRef(true);

  const intl = useIntl();

  useEffect(() => {
    getStars();
  }, []);

  const hubFilterDisabled = isUserAuthorized(aclMatrix.ROUTES, [
    ACL_MATRIX.ROUTES_LIST_ALL_ROUTES
  ]);

  const acceptMethods = (refreshMethod) => {
    refreshTable.current = refreshMethod;
  };

  const handleRefreshTable = () => {
    refreshTable.current({
      pageNumber: currentPage,
      filterValues: selectedFilters
    });
  };

  const handleQuickFilterValue = (value) => {
    let payloadValue = {};
    if (value === QUICK_FILTERS_VALUES.ALL) {
      setCurrentQuickFilter({});

      return {};
    }
    switch (value) {
      case QUICK_FILTERS_VALUES.UNASSIGNED:
      case QUICK_FILTERS_VALUES.ARCHIVED:
      case QUICK_FILTERS_VALUES.DEBREIFED:
      case QUICK_FILTERS_VALUES.DONE:
      case QUICK_FILTERS_VALUES.ASSIGNED:
        payloadValue.states = [value];
        break;
      case QUICK_FILTERS_VALUES.AT_RISK:
      case QUICK_FILTERS_VALUES.INACTIVE:
      case QUICK_FILTERS_VALUES.HIGH_RISK:
        payloadValue[value] = true;
        break;
      default:
        payloadValue.states = [QUICK_FILTERS_VALUES.UNASSIGNED];
    }
    setCurrentQuickFilter(payloadValue);
    return payloadValue;
  };

  const fetchRoutes = async ({ page, filterValues, quickFiltersValue }) => {
    const keys = ['starName', 'name', 'trackingNumber', 'pickupRequestId'];

    const isSearchApplied = keys.some(
      (key) => filterValues?.[key] || selectedFilters?.[key]
    );

    const currentFilters = filterValues || selectedFilters;
    setIsLoading(true);
    let selectedWareHouse = currentFilters?.warehouses;
    if (firstRender.current) {
      setSelectedFilters({
        ...(userWareHouse && { warehouses: userWareHouse })
      });
      selectedWareHouse = userWareHouse ? userWareHouse : selectedWareHouse;
    }

    const createdAt = currentFilters?.createdAt;
    const dispatchedAt = currentFilters?.dispatchedAt;
    const updatedAt = currentFilters?.updatedAt;
    try {
      const payload = {
        ...(searchCleared
          ? {}
          : filterValues
          ? { ...filterValues }
          : { ...selectedFilters }),
        ...(selectedWareHouse && { warehouses: [selectedWareHouse] }),
        pageId: page - 1,
        ...handleQuickFilterValue(searchCleared ? 0 : quickFiltersValue)
      };

      const { result, totalCount } = await getRoutes(payload);

      if (
        (selectedWareHouse || (userWareHouse && firstRender?.current)) &&
        !isSearchApplied
      ) {
        fetchAnalytics({
          warehouseId: firstRender?.current ? userWareHouse : selectedWareHouse,
          createdAt,
          dispatchedAt,
          updatedAt
        });
      } else {
        setAnalytics(null);
      }

      setRoutesCount(totalCount);
      setCurrentPage(page);
      setIsLoading(false);
      setSearchCleared(false);
      firstRender.current = false;
      return {
        list: formatRoutes(result),
        total: totalCount
      };
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAnalytics = async ({
    warehouseId,
    createdAt,
    dispatchedAt,
    updatedAt
  }) => {
    try {
      const { data } = await getRoutesAnalytics({
        warehouseId,
        ...(createdAt && { createdAt }),
        ...(dispatchedAt && { dispatchedAt }),
        ...(updatedAt && { updatedAt })
      });
      setAnalytics(data);
    } catch (error) {
      setAnalytics(null);
    }
  };

  const getStars = async () => {
    setIsLoading(true);
    try {
      const data = await getAllAvailableStars(userWareHouse);

      setOnlineStars(
        formatOptions('_id', 'profile.firstName,profile.lastName', data || [])
      );
    } catch (error) {
      notify(error.message);
    }
    setIsLoading(false);
  };

  const handleOnSubmit = (values) => {
    setSelectedFilters(values);
    refreshTable.current({ pageNumber: 1, filterValues: values });
  };

  const formatRoutes = (data) => {
    return data.map(
      ({
        _id,
        createdAt,
        name,
        deliveryIds,
        pickupRequestIds,
        state,
        updatedAt,
        warehouse,
        star,
        progress,
        ...rest
      }) => {
        return {
          _id,
          key: _id,
          createdAt: (
            <div className="br-routes-listing__created-at-wrapper">
              <span className="body">
                {dayjs(createdAt).format('MMMM D, YYYY')}
              </span>
              <span>{dayjs(createdAt).format('h:mm A')}</span>
            </div>
          ),
          star: star?.name || '-',
          routeAge: intl.formatMessage(
            { id: 'common.timer.hours' },
            {
              hours: Math.floor(
                dayjs.duration(dayjs().diff(dayjs(createdAt))).as('hours')
              )
            }
          ),
          progress: (
            <Progress
              percent={Math.trunc(progress * 100)}
              size={25}
              status="normal"
              className="br-routes-listing__progress"
            />
          ),
          lastUpdatedAt: intl.formatMessage(
            { id: 'common.timer.hours' },
            {
              hours: Math.floor(
                dayjs.duration(dayjs().diff(dayjs(updatedAt))).as('hours')
              )
            }
          ),
          name,
          warehouseName: warehouse?.name,
          numberOfDeliveries: deliveryIds?.length,
          numberOfPickups: pickupRequestIds?.length,
          state,
          stateTag: (
            <BRTag color={ROUTE_STATES_TAG_COLOR[state]}>{state}</BRTag>
          ),
          ...rest
        };
      }
    );
  };

  const renderTableActions = (record) => (
    <RoutesTableActions
      {...record}
      handleRefreshTable={handleRefreshTable}
      onlineStars={onlineStars}
      setIsLoading={setIsLoading}
    />
  );
  const handleExport = async () => {
    try {
      const selectedWareHouse = firstRender.current
        ? userWareHouse
        : selectedFilters?.warehouses;
      const payload = {
        ...selectedFilters,
        ...(selectedWareHouse && { warehouses: [selectedWareHouse] }),
        ...currentQuickFilter
      };
      const { message } = await exportRoutes({
        exportType: ROUTE_EXPORT_TYPE,
        payload
      });
      notify(message, 'success');
    } catch (error) {
      notify(error.message);
    }
  };

  const clearSearch = () => {
    setSearchCleared(true);
  };
  return (
    <Container
      header={
        <BRHeader
          title={intl.formatMessage({
            id: 'routes.routes_listing.title'
          })}
        />
      }
      content={
        <div className="br-routes-listing__container">
          <RoutesSearch
            handleOnSubmit={handleOnSubmit}
            isLoading={isLoading}
            searchCleared={searchCleared}
            loading={isLoading}
            userWareHouse={userWareHouse}
            hubFilterDisabled={!hubFilterDisabled}
            userHubInfo={userHubInfo}
            setSelectedFilters={setSelectedFilters}
          />
          {!!analyltics && (
            <div className="br-routes-listing__analytics-wrapper">
              {ROUTES_ANALYTICS_CARDS(analyltics).map((el) => {
                return <SingleInfoCard title={el.title} data={el?.data} />;
              })}
            </div>
          )}
          <BRTable
            title={intl.formatMessage(
              { id: 'routes.routes_listing.table_title' },
              {
                count: routesCount
              }
            )}
            listFunction={fetchRoutes}
            columns={ROUTES_TABLE_COLUMNS(renderTableActions)}
            quickFilters={ROUTES_TABLE_QUICK_FILTERS({
              selectedHub:
                selectedFilters?.warehouses ||
                (firstRender?.current && userWareHouse)
            })}
            showFilter={false}
            initialQuickFilter={1}
            exportListFileFunction={handleExport}
            shareMethods={acceptMethods}
            doAfterClearSearch={clearSearch}
          />
        </div>
      }
    />
  );
};

export default RoutesListing;
